import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, ChevronRight } from "lucide-react";
import truncateText from "../../utils/truncateText";

const pic = {
  url: "https://res.cloudinary.com/dkcjpdk1c/image/upload/v1767213799/Codehub_hero_background_jvu1gt.jpg",
};

const theme = {
  bgGrainOverlay: "#1E293B",
  bgGrain:
    "https://res.cloudinary.com/dkcjpdk1c/image/upload/v1767214012/sqaure-codehub_qedq9g.png",
};

export interface PageTitleProps {
  title?: string;
  highlights?: React.ReactNode;
  children?: React.ReactNode;
  breadcrumbInside?: boolean;
}

const PageTitle: React.FC<PageTitleProps> = ({
  title,
  highlights,
  children,
  breadcrumbInside = true,
}) => {
  const hasHighlights = Boolean(highlights || children);
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  const toTitleCase = (str: string) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const myStyle: React.CSSProperties = {
    minHeight: hasHighlights ? "260px" : "210px",
    boxShadow: "inset 0 0 0 100vh rgba(0, 0, 0, 0.55)",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: `${theme.bgGrainOverlay}`,
    backgroundImage: `
      linear-gradient(transparent 10px, ${theme.bgGrainOverlay} 800px), 
      url("${theme.bgGrain}"),  
      url("${pic.url}")
    `,
    backgroundPosition: "top, top, top, center",
    backgroundSize: "100% 100%, 75px, 100% 1150px, cover",
    backgroundRepeat: "no-repeat, repeat, no-repeat, no-repeat",
    backgroundBlendMode: "normal, overlay, normal",
  };

  // When breadcrumbs are inside the title banner (default for all public pages)
  if (breadcrumbInside) {
    return (
      <section
        style={myStyle}
        className={`cht-relative ${
          hasHighlights ? "cht-py-10 md:cht-py-14" : "cht-py-8 md:cht-py-12"
        }`}
      >
        <div className="cht-max-w-7xl cht-mx-auto cht-px-4 sm:cht-px-6 lg:cht-px-8 cht-w-full cht-space-y-3.5 md:cht-space-y-4">
          {/* Breadcrumb Navigation Inside Title Banner */}
          <nav
            aria-label="breadcrumb"
            className="cht-flex cht-flex-wrap cht-items-center cht-gap-2 cht-text-xs cht-font-semibold cht-text-white/75"
          >
            <Link
              to="/"
              className="cht-text-white/75 hover:cht-text-white cht-transition-colors cht-no-underline cht-flex cht-items-center cht-gap-1.5"
            >
              <Home size={13} />
              <span>Home</span>
            </Link>

            {pathnames.length === 0 && title && (
              <>
                <ChevronRight size={13} className="cht-text-white/40" />
                <span className="cht-text-white cht-font-bold cht-truncate cht-max-w-xs sm:cht-max-w-md">
                  {title}
                </span>
              </>
            )}

            {pathnames.map((value, index) => {
              const to = `/${pathnames.slice(0, index + 1).join("/")}`;
              const isLast = index === pathnames.length - 1;
              const formattedValue = toTitleCase(value.replace(/-/g, " "));

              return (
                <React.Fragment key={to}>
                  <ChevronRight size={13} className="cht-text-white/40" />
                  {isLast ? (
                    <span className="cht-text-white cht-font-bold cht-truncate cht-max-w-xs sm:cht-max-w-md">
                      {title ? truncateText(title, 35) : truncateText(formattedValue, 25)}
                    </span>
                  ) : (
                    <Link
                      to={to}
                      className="cht-text-white/75 hover:cht-text-white cht-transition-colors cht-no-underline"
                    >
                      {truncateText(formattedValue, 20)}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          {/* Title Heading */}
          <h1 className="!cht-font-heading cht-text-2xl sm:cht-text-3xl md:cht-text-4xl lg:cht-text-5xl !cht-font-extrabold cht-text-white cht-tracking-tight !cht-leading-[1.18] cht-max-w-4xl">
            {title ? title : "Page Title"}
          </h1>

          {/* Highlights / Badges & Metrics Row */}
          {hasHighlights && (
            <div className="cht-pt-1">
              {highlights || children}
            </div>
          )}
        </div>
      </section>
    );
  }

  // Fallback rendering in case breadcrumbInside is explicitly set to false
  return (
    <section style={myStyle} className="cht-py-8 md:cht-py-10">
      <div className="cht-max-w-7xl cht-mx-auto cht-px-4 sm:cht-px-6 lg:cht-px-8 cht-w-full">
        <h1 className="!cht-font-heading cht-text-2xl sm:cht-text-3xl md:cht-text-4xl lg:cht-text-5xl !cht-font-extrabold cht-text-white cht-tracking-tight !cht-leading-[1.18] cht-max-w-4xl">
          {title ? title : "Page Title"}
        </h1>
        {hasHighlights && (
          <div className="cht-pt-2">
            {highlights || children}
          </div>
        )}
      </div>
    </section>
  );
};

export default PageTitle;
